import { SocialNetwork, PlatformPostCounts, BackgroundImage, ImageSource } from '@agency-smm/shared';
import { CardContent } from '../Card';
import { Button } from '../Button';
import { InputWrapper, Label, TextArea, ErrorMessage } from '../Input';
import {
  SOCIAL_NETWORKS,
  SocialNetworkGrid, PlatformRow, PlatformLabel, PlatformName, CountInput,
  ImageSourceSection, ImageSourceGrid, ImageSourceOption, ImageSourceIcon,
  ImageSourceLabel, ImageSourceDesc,
  ImageMixSlider, SliderRow, SliderLabel, SliderValue, RangeSlider,
  ColorPickerContainer, ColorPickerHeader, ColorPickerTitle, ColorInputRow,
  ColorInput, AddColorButton, SelectedColorsContainer, ColorChip, ColorSwatch,
  ColorNumber, RemoveColorButton, NoColorsMessage, SmallButton,
  AssetSelectorContainer, AssetSelectorHeader, AssetSelectorTitle,
  AssetSelectorButtons, AssetSelector, AssetItem, AssetThumb, AssetName,
} from './styled';

interface LegacyPlatformControlsProps {
  platformCounts: PlatformPostCounts;
  totalPostCount: number;
  togglePlatform: (p: SocialNetwork) => void;
  updatePlatformCount: (p: SocialNetwork, count: number) => void;

  enabledImageSources: ImageSource[];
  imageMix: Record<ImageSource, number>;
  toggleImageSource: (source: ImageSource) => void;
  updateImageMix: (source: ImageSource, value: number) => void;

  customColors: string[];
  colorPickerValue: string;
  setColorPickerValue: (v: string) => void;
  addCustomColor: () => void;
  removeCustomColor: (index: number) => void;
  clearAllColors: () => void;

  backgroundImages: BackgroundImage[];
  selectedAssetIds: string[];
  toggleAssetSelection: (id: string) => void;
  selectAllAssets: () => void;
  deselectAllAssets: () => void;

  customPrompt: string;
  setCustomPrompt: (v: string) => void;

  error: string;
  templateError: string;
  isGenerating: boolean;
  isStreaming: boolean;
  userCredits: number;
  onGenerate: () => void;
}

/**
 * Legacy platform/source selector. Está oculto en producción (Novah maneja
 * la config) pero se conserva como fallback — sigue conectado al state vivo,
 * solo no se renderiza visible. Si querés removerlo definitivamente, sacalo
 * de GeneratePage.
 */
export function LegacyPlatformControls(props: LegacyPlatformControlsProps) {
  const {
    platformCounts, totalPostCount, togglePlatform, updatePlatformCount,
    enabledImageSources, imageMix, toggleImageSource, updateImageMix,
    customColors, colorPickerValue, setColorPickerValue, addCustomColor,
    removeCustomColor, clearAllColors,
    backgroundImages, selectedAssetIds, toggleAssetSelection, selectAllAssets,
    deselectAllAssets,
    customPrompt, setCustomPrompt,
    error, templateError, isGenerating, isStreaming, userCredits, onGenerate,
  } = props;

  return (
    <div style={{ display: 'none' }}>
      <CardContent>
        <InputWrapper style={{ marginBottom: 16 }}>
          <Label>Platforms & Post Counts</Label>
          <SocialNetworkGrid>
            {SOCIAL_NETWORKS.map((network) => {
              const isActive = platformCounts[network.id] !== undefined;
              return (
                <PlatformRow key={network.id} $active={isActive}>
                  <PlatformLabel>
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={() => togglePlatform(network.id)}
                    />
                    <PlatformName>{network.label}</PlatformName>
                  </PlatformLabel>
                  <CountInput
                    type="number"
                    min={0}
                    max={20}
                    value={platformCounts[network.id] || 0}
                    onChange={(e) => updatePlatformCount(network.id, Number(e.target.value))}
                    disabled={!isActive}
                  />
                </PlatformRow>
              );
            })}
          </SocialNetworkGrid>
          <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#525252', fontWeight: 600 }}>
            Total posts: {totalPostCount}
          </div>
        </InputWrapper>

        <ImageSourceSection>
          <Label>Image Sources</Label>
          <ImageSourceGrid>
            <ImageSourceOption type="button" $active={enabledImageSources.includes('stock')} onClick={() => toggleImageSource('stock')}>
              <ImageSourceIcon>📷</ImageSourceIcon>
              <ImageSourceLabel>Stock Photos</ImageSourceLabel>
              <ImageSourceDesc>From Pexels</ImageSourceDesc>
            </ImageSourceOption>
            <ImageSourceOption type="button" $active={enabledImageSources.includes('pastel')} onClick={() => toggleImageSource('pastel')}>
              <ImageSourceIcon>🎨</ImageSourceIcon>
              <ImageSourceLabel>Colored</ImageSourceLabel>
              <ImageSourceDesc>Solid backgrounds</ImageSourceDesc>
            </ImageSourceOption>
            <ImageSourceOption
              type="button"
              $active={enabledImageSources.includes('asset')}
              $disabled={backgroundImages.length === 0}
              disabled={backgroundImages.length === 0}
              onClick={() => toggleImageSource('asset')}
            >
              <ImageSourceIcon>🖼️</ImageSourceIcon>
              <ImageSourceLabel>Your Images</ImageSourceLabel>
              <ImageSourceDesc>{backgroundImages.length} available</ImageSourceDesc>
            </ImageSourceOption>
            <ImageSourceOption type="button" $active={enabledImageSources.includes('ai')} onClick={() => toggleImageSource('ai')}>
              <ImageSourceIcon>🤖</ImageSourceIcon>
              <ImageSourceLabel>AI Generated</ImageSourceLabel>
              <ImageSourceDesc>Stable Diffusion</ImageSourceDesc>
            </ImageSourceOption>
          </ImageSourceGrid>

          {enabledImageSources.length > 1 && (
            <ImageMixSlider>
              <div style={{ fontSize: '0.75rem', color: '#525252', marginBottom: 8, fontWeight: 600 }}>
                Distribution (adjust how many of each type)
              </div>
              {(['stock', 'pastel', 'asset', 'ai'] as ImageSource[]).map((src) => (
                enabledImageSources.includes(src) && (
                  <SliderRow key={src}>
                    <SliderLabel>{src === 'stock' ? 'Stock' : src === 'pastel' ? 'Colored' : src === 'asset' ? 'Your Images' : 'AI Generated'}</SliderLabel>
                    <RangeSlider
                      type="range"
                      min={0}
                      max={100}
                      value={imageMix[src]}
                      onChange={(e) => updateImageMix(src, Number(e.target.value))}
                    />
                    <SliderValue>{imageMix[src]}%</SliderValue>
                  </SliderRow>
                )
              ))}
            </ImageMixSlider>
          )}

          {enabledImageSources.includes('pastel') && (
            <ColorPickerContainer>
              <ColorPickerHeader>
                <ColorPickerTitle>
                  Custom Colors {customColors.length > 0 && `(${customColors.length})`}
                </ColorPickerTitle>
                {customColors.length > 0 && (
                  <SmallButton onClick={clearAllColors}>Clear All</SmallButton>
                )}
              </ColorPickerHeader>
              <ColorInputRow>
                <ColorInput
                  type="color"
                  value={colorPickerValue}
                  onChange={(e) => setColorPickerValue(e.target.value)}
                />
                <AddColorButton onClick={addCustomColor}>Add Color</AddColorButton>
              </ColorInputRow>
              <SelectedColorsContainer>
                {customColors.length === 0 ? (
                  <NoColorsMessage>
                    No colors selected. Random colors will be used.
                  </NoColorsMessage>
                ) : (
                  customColors.map((color, index) => (
                    <ColorChip key={`${color}-${index}`} $color={color}>
                      <ColorNumber>{index + 1}</ColorNumber>
                      <ColorSwatch $color={color} />
                      <span>{color.toUpperCase()}</span>
                      <RemoveColorButton onClick={() => removeCustomColor(index)}>
                        ×
                      </RemoveColorButton>
                    </ColorChip>
                  ))
                )}
              </SelectedColorsContainer>
            </ColorPickerContainer>
          )}

          {enabledImageSources.includes('asset') && backgroundImages.length > 0 && (
            <AssetSelectorContainer>
              <AssetSelectorHeader>
                <AssetSelectorTitle>
                  Select Images ({selectedAssetIds.length}/{backgroundImages.length})
                </AssetSelectorTitle>
                <AssetSelectorButtons>
                  <SmallButton onClick={selectAllAssets}>Select All</SmallButton>
                  <SmallButton onClick={deselectAllAssets}>Clear</SmallButton>
                </AssetSelectorButtons>
              </AssetSelectorHeader>
              <AssetSelector>
                {backgroundImages.map((asset) => (
                  <AssetItem
                    key={asset.id}
                    $selected={selectedAssetIds.includes(asset.id)}
                    onClick={() => toggleAssetSelection(asset.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAssetIds.includes(asset.id)}
                      readOnly
                    />
                    <AssetThumb src={asset.url} alt={asset.name} />
                    <AssetName>{asset.name}</AssetName>
                  </AssetItem>
                ))}
              </AssetSelector>
            </AssetSelectorContainer>
          )}

          {enabledImageSources.includes('asset') && backgroundImages.length === 0 && (
            <AssetSelectorContainer>
              <AssetSelectorHeader>
                <AssetSelectorTitle style={{ color: '#EF4444' }}>
                  No images uploaded yet
                </AssetSelectorTitle>
              </AssetSelectorHeader>
              <div style={{ padding: '12px', fontSize: '0.85rem', color: '#525252' }}>
                Go to your Workspace page and upload images in the "Background Images" section to use them here.
              </div>
            </AssetSelectorContainer>
          )}
        </ImageSourceSection>

        <InputWrapper style={{ marginBottom: 16 }}>
          <Label htmlFor="customPrompt">Custom Instructions (optional)</Label>
          <TextArea
            id="customPrompt"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="e.g., Focus on summer promotions, use casual tone..."
          />
        </InputWrapper>

        {(error || templateError) && <ErrorMessage style={{ marginBottom: 16 }}>{error || templateError}</ErrorMessage>}
        <Button
          $fullWidth
          onClick={onGenerate}
          disabled={isGenerating || isStreaming || totalPostCount === 0 || userCredits < totalPostCount}
        >
          {isGenerating || isStreaming
            ? 'Generating...'
            : userCredits < totalPostCount
              ? `Not enough credits (need ${totalPostCount}, have ${userCredits})`
              : `Generate ${totalPostCount} Posts (${totalPostCount} credits)`}
        </Button>
      </CardContent>
    </div>
  );
}
